// src/components/AccessGuard.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message, Spin } from 'antd';
import { checkMyAccess } from '../api/userAPI';

/**
 * AccessGuard 访问控制组件
 * —— 用法（强烈推荐“包裹”整页，避免未校验完成的闪屏）——
 *   export default function SomePage() {
 *     return (
 *       <AccessGuard allowedRoles={['admin','scheduler','referee_admin']}>
 *         <页面原本的 JSX />
 *       </AccessGuard>
 *     );
 *   }
 *
 * —— 也可在 return 顶部自闭合使用（改动最少，但可能有 1-2 帧闪屏）——
 *   <>
 *     <AccessGuard allowedRoles={['admin','scheduler','referee_admin']} />
 *     <页面原本的 JSX />
 *   </>
 *
 * Props:
 *  - allowedRoles: string[] 允许访问的角色 key，默认 ['admin','scheduler','referee_admin']
 *  - requiredCapabilities: string[] 允许访问所需能力（与 allowedRoles 二选一，或同时使用）
 *  - requireAllCapabilities: boolean requiredCapabilities 是否要求“全部满足”，默认 false（满足任一即可）
 *  - routeKey: string 通过后端配置里的 routes 规则进行校验（可选）
 *  - fallbackPath: string    无权限时的跳转路径，默认 '/home'
 *  - messageText:  string    无权限提示语，默认 '请向管理员申请访问权限'
 *  - matchID:      number    传入当前比赛ID。若不传，会尝试从 URL params 里取 {id}
 *  - allowAnyMatchRole: boolean  无 matchID 时，是否允许“任一赛事下已审批角色”通过（默认 false）
 *
 * 说明：
 *  1) 组件不再依赖 localStorage.role；
 *  2) 每次进入页面都调用后端 /api/users/me/access-check，基于 req.user + User_Match_Roles 判定；
 *  3) 判定不通过则提示并跳转到 fallbackPath。
 */
export default function AccessGuard({
  allowedRoles = ['admin', 'scheduler', 'referee_admin'],
  requiredCapabilities = [],
  requireAllCapabilities = false,
  routeKey = '',
  fallbackPath = '/home',
  messageText = '请向管理员申请访问权限',
  matchID: matchIDProp,
  allowAnyMatchRole = false,
  children
}) {
  const navigate = useNavigate();
  const params = useParams();
  const [checking, setChecking] = useState(true);  // 是否正在校验
  const [allowed, setAllowed] = useState(false);   // 是否通过校验
  const toastOnceRef = useRef(false);              // 防止重复弹 toast

  // —— 解析 matchID：优先 props，其次 URL params —— //
  const matchID = useMemo(() => {
    if (matchIDProp != null) {
      const n = Number(matchIDProp);
      return Number.isFinite(n) ? n : null;
    }
    const raw = params?.matchID ?? params?.id;
    if (raw != null) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, [matchIDProp, params]);

  const normalizedAllowedRoles = useMemo(() => {
    return Array.from(new Set(
      (Array.isArray(allowedRoles) ? allowedRoles : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    ));
  }, [allowedRoles]);
  const allowedRolesKey = useMemo(
    () => normalizedAllowedRoles.join('|'),
    [normalizedAllowedRoles]
  );
  const normalizedAllowedRolesStable = useMemo(
    () => (allowedRolesKey ? allowedRolesKey.split('|') : []),
    [allowedRolesKey]
  );
  const normalizedRequiredCapabilities = useMemo(() => {
    return Array.from(new Set(
      (Array.isArray(requiredCapabilities) ? requiredCapabilities : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    ));
  }, [requiredCapabilities]);
  const requiredCapabilitiesKey = useMemo(
    () => normalizedRequiredCapabilities.join('|'),
    [normalizedRequiredCapabilities]
  );
  const normalizedRequiredCapabilitiesStable = useMemo(
    () => (requiredCapabilitiesKey ? requiredCapabilitiesKey.split('|') : []),
    [requiredCapabilitiesKey]
  );
  const normalizedRouteKey = useMemo(
    () => String(routeKey || '').trim(),
    [routeKey]
  );
  const usesCapabilityMode = useMemo(
    () => normalizedRequiredCapabilitiesStable.length > 0 || !!normalizedRouteKey,
    [normalizedRequiredCapabilitiesStable, normalizedRouteKey]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!usesCapabilityMode && !normalizedAllowedRolesStable.length) {
          if (!cancelled) {
            setAllowed(false);
            setChecking(false);
          }
          return;
        }
        if (usesCapabilityMode && !normalizedAllowedRolesStable.length && !normalizedRequiredCapabilitiesStable.length && !normalizedRouteKey) {
          if (!cancelled) {
            setAllowed(false);
            setChecking(false);
          }
          return;
        }
        setChecking(true);
        const resp = await checkMyAccess({
          allowedRoles: normalizedAllowedRolesStable,
          matchID: Number.isFinite(Number(matchID)) && Number(matchID) > 0 ? Number(matchID) : null,
          allowAnyMatchRole: Boolean(allowAnyMatchRole),
          requiredCapabilities: normalizedRequiredCapabilitiesStable,
          requireAllCapabilities: Boolean(requireAllCapabilities),
          routeKey: normalizedRouteKey,
        });
        if (!cancelled) {
          setAllowed(Boolean(resp?.data?.allowed));
        }
      } catch (err) {
        if (!cancelled) {
          setAllowed(false);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    matchID,
    allowedRolesKey,
    allowAnyMatchRole,
    normalizedAllowedRolesStable,
    requiredCapabilitiesKey,
    normalizedRequiredCapabilitiesStable,
    requireAllCapabilities,
    normalizedRouteKey,
    usesCapabilityMode,
  ]);

  // —— 未通过则提示并跳转（用 useNavigate） —— //
  useEffect(() => {
    if (checking) return;
    if (allowed) return;
    if (toastOnceRef.current) return;

    toastOnceRef.current = true;
    message.error(messageText);
    // 说明：useNavigate 属于 SPA 内部跳转，不刷新整页，更快，不丢失前端状态
    navigate(fallbackPath, { replace: true });
  }, [checking, allowed, fallbackPath, messageText, navigate]);

  // —— 加载态（避免未校验完成时闪屏） —— //
  if (checking) {
    return (
      <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '15vh 0'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Spin size="large" />
          <div style={{ color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>正在校验访问权限...</div>
        </div>
      </div>
    );
  }

  // —— 通过：展示子内容；未通过：此时已触发跳转，这里返回 null —— //
  if (!allowed) return null;
  return <>{children}</>;
}
